"use client";
/**
 * usePreferences — V4.0 Phase 2 Chantier 4
 *
 * Hook React pour gérer les préférences utilisateur en localStorage.
 * Persisté côté client uniquement (pas de backend pour l'instant).
 * Format stable pour migration future vers SQLite/API.
 */
import { useState, useCallback, useEffect } from 'react';

const PREFS_KEY = 'voyage_user_preferences';

export interface UserPreferences {
  /** Profil de scoring par défaut */
  profil: 'best-value' | 'cheap-max' | 'fastest' | 'low-friction' | 'anti-arnaque';
  /** Budget max par défaut (€/pers) */
  budgetDefault: number;
  /** Nombre de voyageurs par défaut */
  voyageursDefault: number;
  /** Éviter les départs avant cette heure (0-23, 0 = pas de contrainte) */
  departMinHeure: number;
  /** Max correspondances acceptées (0 = direct seul, 99 = pas de limite) */
  maxCorrespondances: number;
  /** Mode de transport préféré */
  modePreference: 'indifferent' | 'train' | 'avion';
  /** Éviter les aéroports secondaires */
  eviterExcentres: boolean;
  /** Bagage cabine uniquement (filtrer les LCC sans soute) */
  bagageCabineOnly: boolean;
  /** Ville d'origine par défaut */
  origineDefault: string;
}

export const DEFAULT_PREFERENCES: UserPreferences = {
  profil: 'best-value',
  budgetDefault: 500,
  voyageursDefault: 2,
  departMinHeure: 0,
  maxCorrespondances: 99,
  modePreference: 'indifferent',
  eviterExcentres: false,
  bagageCabineOnly: false,
  origineDefault: 'Paris',
};

// Audit 23 P3 : validation minimale du localStorage parsé
function _isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function loadPrefs(): UserPreferences {
  if (typeof window === 'undefined') return { ...DEFAULT_PREFERENCES };
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (!_isPlainObject(parsed)) return { ...DEFAULT_PREFERENCES };
      // Merge avec les defaults pour les nouveaux champs ajoutés après la première save
      return { ...DEFAULT_PREFERENCES, ...parsed };
    }
  } catch { /* ignore */ }
  return { ...DEFAULT_PREFERENCES };
}

function savePrefs(prefs: UserPreferences): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch { /* ignore — localStorage plein ou désactivé */ }
}

export function usePreferences() {
  const [prefs, setPrefsState] = useState<UserPreferences>(loadPrefs);

  // Sync across tabs (StorageEvent)
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === PREFS_KEY && e.newValue) {
        try {
          const p = JSON.parse(e.newValue);
          if (_isPlainObject(p)) setPrefsState({ ...DEFAULT_PREFERENCES, ...p });
        } catch { /* ignore */ }
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const setPrefs = useCallback((update: Partial<UserPreferences>) => {
    setPrefsState(prev => {
      const next = { ...prev, ...update };
      savePrefs(next);
      return next;
    });
  }, []);

  const resetPrefs = useCallback(() => {
    const defaults = { ...DEFAULT_PREFERENCES };
    savePrefs(defaults);
    setPrefsState(defaults);
  }, []);

  return { prefs, setPrefs, resetPrefs };
}
